import React, { useState } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { media } from "../theme/breakpoints";
import StatusPill from "../components/StatusPill";
import Button from "../components/Button";
import { fmtINR, fmtDate } from "../utils/formatters";
import {
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import OnsitePaymentFlow from "../OnsitePayment";
import AdminCancelModal from "../modals/AdminCancelModal";
import BookingService from "../../../services/BookingService";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import useRazorPay from "../../../hooks/useRazorPay";
import RescheduleModal from "../modals/RescheduleModal";
import { downloadInvoice } from "../data/mockBookings";
import { getErrorMessage } from "../../../helpers/functions";

const BookingDetail = ({
  booking,
  onClose,
  onRecordOnsite,
  onAdminCancel,
  MTRef,
}) => {
  const [onsiteOpen, setOnsiteOpen] = useState(false);
  const [adminCancelOpen, setAdminCancelOpen] = useState(false);
  const [onsiteBooking, setOnsiteBooking] = useState(null);
  const [adminCancelBooking, setAdminCancelBooking] = useState(null);
  const [openReschedule, setOpenReschedule] = useState({ state: false });
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const openRazorPay = useRazorPay();

  const [priceDiffModal, setPriceDiffModal] = useState({
    open: false,
    priceDiff: 0,
    gstOnDiff: 0,
    totalOwed: 0,
    values: null,
    paymentType: "offline",
  });

  if (!booking) return null;

  const b = {
    ...booking,
    ref: booking.booking_reference || "N/A",
    status: booking.booking_status || "reserved",
    paymentStatus: booking.payment_status || "pending",
    date: booking.hearing_date,
    startTime: booking.start_time?.slice(0, 5) || "--:--",
    endTime: booking.end_time?.slice(0, 5) || "--:--",
    duration: booking.session_duration === "fullday" ? "6hr" : "3hr",
    room: {
      name: booking.hearing_room || "Unknown Room",
      code: booking.hearing_room || "",
    },
    totals: {
      total: booking.total_amount || 0,
    },
    createdAt: booking.created_at,
    bookedBy: booking.owner_details || {
      name: "N/A",
      email: "",
      phone: "",
    },
    participants: {
      claimants: booking.claimants_list || [],
      respondents: booking.respondents_list || [],
      arbitrators: booking.arbitrators_list || [],
    },
    bookedMode: "online",
  };

  const isActive = b.status === "confirmed" || b.status === "reserved";
  const dateObj = new Date(b.date);

  const grandTotal = booking.grand_total || booking.total_amount || 0;
  const bookingTotal = booking.total_amount || 0;
  const onsiteTotal = booking.onsite_total || 0;

  const handleRecordOnsite = () => {
    setOnsiteBooking(b);
    setOnsiteOpen(true);
  };

  const handleCloseOnsite = () => {
    setOnsiteOpen(false);
    setOnsiteBooking(null);
  };

  const handleAdminCancel = () => {
    setAdminCancelBooking(b);
    setAdminCancelOpen(true);
  };

  const handleReshedule = () => {
    setOpenReschedule({ state: true, metaData: b });
  };

  async function handleCancelConfirm(data) {
    try {
      setLoader({ state: true, message: "adding" });
      const response = await BookingService.cancelBooking(data);
      if (response) {
        enqueueSnackbar(response?.message, { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
      setAdminCancelOpen(false);
      setAdminCancelBooking(null);
      onClose();
    }
  }

  async function capturePayment(rzp_res) {
    document.body.style.overflow = null;
    try {
      setLoader({ state: true, message: "Capturing Payment..." });
      const capture_res = await BookingService.capturePayment(rzp_res);
      if (capture_res) {
        enqueueSnackbar(capture_res.message, { variant: "success" });
        downloadInvoice(capture_res?.invoice_url);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  async function onFormSubmit(data) {
    try {
      setLoader({ state: true, message: "Adding onsite service..." });
      const response = await BookingService.addOnsiteService(data);
      if (data?.modeOfPayment === "razorpayLink") {
        openRazorPay({
          order_id: response.razorpayOrderId,
          handler: capturePayment,
        });
        return;
      }
      if (response) {
        enqueueSnackbar(response?.message, { variant: "success" });
        downloadInvoice(response?.invoice_url);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
      setOnsiteOpen(false);
      onClose();
      if (MTRef?.current) {
        MTRef.current.onQueryChange("");
      }
    }
  }

  const isMeetingOngoing = (() => {
    if (!booking?.hearing_date || !booking?.start_time || !booking?.end_time) {
      return false;
    }
    const now = new Date();
    const startDateTime = new Date(
      `${booking.hearing_date}T${booking.start_time}`,
    );
    const endDateTime = new Date(`${booking.hearing_date}T${booking.end_time}`);
    return now >= startDateTime && now <= endDateTime;
  })();

  const buildPayload = (values, extra = {}) => ({
    booking_reference: values.booking_reference || values.ref,
    hearing_date: values.hearing_date,
    start_time: values.start_time,
    session_duration: values.session_duration,
    hearing_room: values.hearing_room?.roomName,
    ...extra,
  });

  const closePriceDiffModal = () => {
    setPriceDiffModal((prev) => ({ ...prev, open: false }));
  };

  async function captureReschedulePayment(rzp_res) {
    document.body.style.overflow = null;
    try {
      setLoader({ state: true, message: "Capturing Payment..." });
      const capture_res =
        await BookingService.captureReschedulePayment(rzp_res);
      if (capture_res) {
        enqueueSnackbar(
          capture_res?.message || "Payment successful, booking rescheduled.",
          { variant: "success" },
        );
        if (capture_res?.invoice_url) {
          downloadInvoice(capture_res.invoice_url);
        }
      }
      if (MTRef?.current) MTRef.current.onQueryChange("");
      closePriceDiffModal();
      onClose();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const launchRescheduleRazorpay = (res) => {
    if (!res?.razorpayOrderId) {
      enqueueSnackbar("Unable to create Razorpay order.", {
        variant: "error",
      });
      setLoader({ state: false });
      return;
    }
    openRazorPay({
      order_id: res.razorpayOrderId,
      handler: async (rzp_res) => {
        await captureReschedulePayment(rzp_res);
      },
      modal: {
        ondismiss: async () => {
          document.body.style.overflow = null;
          setLoader({ state: false });
          try {
            await BookingService.quitPaymentService({
              booking_id: res?.booking_id,
            });
            enqueueSnackbar("Payment cancelled.", { variant: "info" });
          } catch (error) {
            enqueueSnackbar(
              getErrorMessage(error) || "Unable to cancel payment.",
              { variant: "error" },
            );
          }
        },
      },
    });
  };

  const finalizeReschedule = async (values, paymentType) => {
    if (!values) {
      enqueueSnackbar("Something went wrong. Please retry rescheduling.", {
        variant: "error",
      });
      return;
    }

    try {
      setLoader({ state: true, message: "Rescheduling booking..." });

      if (paymentType === "online") {
        const orderRes = await BookingService.rescheduleBooking(
          buildPayload(values, { typeOfPayment: "online" }),
        );
        launchRescheduleRazorpay(orderRes);
        return;
      }

      if (paymentType === "offline") {
        const res = await BookingService.rescheduleBooking(
          buildPayload(values, {
            typeOfPayment: "offline",
            modeOfPayment: "cash",
          }),
        );
        enqueueSnackbar(res?.message || "Booking rescheduled successfully.", {
          variant: "success",
        });
        if (res?.invoice_url) downloadInvoice(res.invoice_url);
        if (MTRef?.current) MTRef.current.onQueryChange("");
        closePriceDiffModal();
        onClose();
        return;
      }

      if (paymentType === "offline-refund") {
        const res = await BookingService.rescheduleBooking(
          buildPayload(values, { refundMode: "offline" }),
        );

        enqueueSnackbar(
          res?.message ||
            "Booking rescheduled. Refund is in progress and needs staff confirmation.",
          { variant: "success" },
        );

        if (MTRef?.current) MTRef.current.onQueryChange("");
        closePriceDiffModal();
        return;
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      if (paymentType !== "online") {
        setLoader({ state: false });
      }
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={true}
        onClose={onClose}
        disableRestoreFocus
        disableEnforceFocus
        ModalProps={{
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true,
          keepMounted: true,
        }}
      >
        <Header>
          <HeaderTop>
            <HeaderTopMeta>
              <RefRow>
                <RefText>{b.ref}</RefText>
                <StatusPill status={b.status} />
                <StatusPill status={b.paymentStatus} />
              </RefRow>
              <SubText>Booked {fmtDate(b.createdAt)} • Online</SubText>
            </HeaderTopMeta>
          </HeaderTop>

          <SessionCard>
            <DateBlock>
              <DateDay>{dateObj.getDate()}</DateDay>
              <DateMonth>
                {dateObj.toLocaleDateString("en-IN", {
                  month: "short",
                  year: "numeric",
                })}
              </DateMonth>
            </DateBlock>

            <SessionInfo>
              <SessionRoom>{b.room.name}</SessionRoom>
              <SessionMeta>
                {b.startTime} – {b.endTime} •{" "}
                {b.duration === "6hr" ? "Full Day" : "Half Day"}
              </SessionMeta>
            </SessionInfo>

            <Amount>
              <AmountValue>{fmtINR(grandTotal)}</AmountValue>
              <AmountSub>Total Booking</AmountSub>
            </Amount>
          </SessionCard>
        </Header>

        <Body>
          {isActive &&
            (b.payment_status === "paid" || b.payment_status === "confirm") && (
              <ActionRow>
                <Button variant="gold" size="md" onClick={handleRecordOnsite}>
                  Record On-Site Payment
                </Button>
                {!isMeetingOngoing && (
                  <>
                    <Button
                      variant="danger"
                      size="md"
                      onClick={handleAdminCancel}
                    >
                      Cancel Booking
                    </Button>

                    <Button
                      variant="primary"
                      size="md"
                      onClick={handleReshedule}
                    >
                      Reschedule
                    </Button>
                  </>
                )}
              </ActionRow>
            )}

          {/* Booked By */}
          <Section>
            <SectionHeader>Booked By</SectionHeader>
            <InfoCard>
              <ContactGrid>
                <div>
                  <FieldLabel>Name</FieldLabel>
                  <FieldValue>{b.bookedBy.name}</FieldValue>
                </div>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  <FieldValue>{b.bookedBy.email || "—"}</FieldValue>
                </div>
                <div>
                  <FieldLabel>Phone</FieldLabel>
                  <FieldValue>
                    {b.bookedBy.mobile || b.bookedBy.phone || "—"}
                  </FieldValue>
                </div>
              </ContactGrid>
            </InfoCard>
          </Section>

          {/* Participants */}
          <Section>
            <SectionHeader>Participants</SectionHeader>
            {["claimants", "respondents", "arbitrators"].map((kind) => {
              const label = kind.charAt(0).toUpperCase() + kind.slice(1);
              const list = b.participants[kind] || [];
              return (
                <ParticipantGroup key={kind}>
                  <ParticipantRoleHeader>
                    {label} ({list.length})
                  </ParticipantRoleHeader>
                  {list.map((p, i) => (
                    <ParticipantCard key={i}>
                      <ParticipantText $strong>
                        {p.Name || p.name}
                      </ParticipantText>
                      <ParticipantText>
                        {p.Email || p.email || "—"}
                      </ParticipantText>
                      <ParticipantText>
                        {p.Phone || p.phone || "—"}
                      </ParticipantText>
                    </ParticipantCard>
                  ))}
                </ParticipantGroup>
              );
            })}
          </Section>

          {/* Charges */}
          <Section>
            <SectionHeader>Charges Breakdown</SectionHeader>
            <InfoCard>
              <KVBox>
                <KVRow>
                  <KVLabel style={{ color: "#293461" }}>
                    <strong>Booking Charges (Prepaid)</strong>
                  </KVLabel>
                  <KVValue style={{ color: "#00838c" }}>
                    {fmtINR(bookingTotal)}
                  </KVValue>
                </KVRow>

                <KVRow>
                  <KVLabel>Room Charge</KVLabel>
                  <KVValue>{fmtINR(booking.room_charge || 0)}</KVValue>
                </KVRow>

                {booking.service_charge?.map((s, i) => (
                  <KVRow key={`booking-${i}`}>
                    <KVLabel>{s.name}</KVLabel>
                    <KVValue>
                      {s.price === "ON_REQUEST"
                        ? "On Request"
                        : fmtINR(s.price)}
                    </KVValue>
                  </KVRow>
                ))}

                <KVRow>
                  <KVLabel>Booking GST (@18%)</KVLabel>
                  <KVValue>{fmtINR(booking.gst_amount || 0)}</KVValue>
                </KVRow>

                <Divider />

                {onsiteTotal > 0 && (
                  <>
                    <KVRow>
                      <KVLabel style={{ color: "#293461" }}>
                        <strong>On-Site Services (Added Later)</strong>
                      </KVLabel>
                      <KVValue style={{ color: "#00838c" }}>
                        {fmtINR(onsiteTotal)}
                      </KVValue>
                    </KVRow>

                    {booking.onsite_services?.map((s, i) => (
                      <KVRow key={`onsite-${i}`}>
                        <KVLabel>
                          {s.name} {s.qty > 1 ? `× ${s.qty}` : ""}
                        </KVLabel>
                        <KVValue>
                          {fmtINR((s.price || 0) * (s.qty || 1))}
                        </KVValue>
                      </KVRow>
                    ))}

                    <KVRow>
                      <KVLabel>Onsite GST (@18%)</KVLabel>
                      <KVValue>{fmtINR(booking.onsite_gst || 0)}</KVValue>
                    </KVRow>

                    <Divider />
                  </>
                )}

                <KVRow style={{ marginTop: "8px" }}>
                  <KVLabel>
                    <strong>Grand Total Paid</strong>
                  </KVLabel>
                  <KVValue>
                    <strong
                      style={{ fontSize: "17px", color: COLORS.BTN_GREEN }}
                    >
                      {fmtINR(grandTotal)}
                    </strong>
                  </KVValue>
                </KVRow>
              </KVBox>
            </InfoCard>
          </Section>

          {/* Invoices */}
          <Section>
            <SectionHeader>Invoices</SectionHeader>
            {b.bookingRoomInvoice && b.bookingRoomInvoice.length > 0 ? (
              b.bookingRoomInvoice.map((inv, i) => (
                <InvoiceCard key={i}>
                  <InvoiceLeft>
                    <InvoiceMeta>
                      <InvoiceNumber>{inv.invoice_number}</InvoiceNumber>
                      <InvoiceType>
                        {inv.invoice_type === "onSite"
                          ? "Onsite Invoice"
                          : "Booking Invoice"}
                      </InvoiceType>
                    </InvoiceMeta>
                  </InvoiceLeft>
                  <InvoiceRight>
                    <InvoiceAmount>
                      <InvoiceAmountValue>
                        {fmtINR(inv.invoice_amount)}
                      </InvoiceAmountValue>
                    </InvoiceAmount>
                    {inv.invoice_url && (
                      <PDFButton
                        onClick={() => downloadInvoice(inv.invoice_url)}
                      >
                        Download Invoice
                      </PDFButton>
                    )}
                  </InvoiceRight>
                </InvoiceCard>
              ))
            ) : (
              <EmptyState>No invoices generated yet</EmptyState>
            )}
          </Section>
        </Body>
      </Drawer>

      {/* Modals */}
      {onsiteOpen && (
        <OnsitePaymentFlow
          bookings={[b]}
          preSelectedBooking={onsiteBooking}
          onClose={handleCloseOnsite}
          onComplete={(data) => onFormSubmit(data)}
        />
      )}
      {adminCancelOpen && adminCancelBooking && (
        <AdminCancelModal
          booking={adminCancelBooking}
          onClose={() => setAdminCancelOpen(false)}
          onConfirm={handleCancelConfirm}
        />
      )}

      {openReschedule?.state && (
        <RescheduleModal
          open={openReschedule?.state}
          onClose={() => setOpenReschedule({ state: false })}
          booking={openReschedule?.metaData}
          onSubmit={async (values) => {
            try {
              setLoader({ state: true, message: "Rescheduling booking..." });
              const res = await BookingService.rescheduleBooking(
                buildPayload(values),
              );
              enqueueSnackbar(
                res?.message || "Booking rescheduled successfully.",
                { variant: "success" },
              );
              if (MTRef?.current) MTRef.current.onQueryChange("");
              onClose();
            } catch (error) {
              const data = error?.response?.data || error;
              if (
                data &&
                typeof data?.priceDiff === "number" &&
                data.priceDiff !== 0
              ) {
                // Refunds (priceDiff < 0) include GST, so both cases are
                // shown to the case manager before confirming.
                const isRefund = data.priceDiff < 0;
                setPriceDiffModal({
                  open: true,
                  isRefund,
                  priceDiff: Math.abs(data.priceDiff),
                  gstOnDiff: data.gstOnDiff,
                  totalOwed: isRefund ? data.refundOwed : data.totalOwed,
                  newRoomCharge: data.new_room_charge,
                  serviceChargeTotal: data.service_charge_total,
                  creditApplied: data.credit_applied,
                  policyApplied: data.policy_applied,
                  values,
                  paymentType: isRefund ? "offline-refund" : "offline",
                });
              } else {
                enqueueSnackbar(getErrorMessage(error), { variant: "error" });
              }
            } finally {
              setLoader({ state: false });
            }
          }}
        />
      )}

      {priceDiffModal.open && (
        <StyledDialog
          open={priceDiffModal.open}
          onClose={closePriceDiffModal}
          keepMounted={false}
          disableRestoreFocus
        >
          <DialogTitle>
            <ModalTitle>
              {priceDiffModal.isRefund
                ? "Refund Due to Customer"
                : "Extra Payment Required"}
            </ModalTitle>
          </DialogTitle>
          <DialogContent>
            <ModalAmountBanner>
              {typeof priceDiffModal.newRoomCharge === "number" && (
                <>
                  <ModalAmountRow>
                    <span>Room charge (new date)</span>
                    <strong>{fmtINR(priceDiffModal.newRoomCharge)}</strong>
                  </ModalAmountRow>
                  {priceDiffModal.serviceChargeTotal > 0 && (
                    <ModalAmountRow>
                      <span>Booked services</span>
                      <strong>{fmtINR(priceDiffModal.serviceChargeTotal)}</strong>
                    </ModalAmountRow>
                  )}
                  <ModalAmountRow>
                    <span>Less: credit from current booking</span>
                    <strong>- {fmtINR(priceDiffModal.creditApplied || 0)}</strong>
                  </ModalAmountRow>
                  <ModalAmountDivider />
                </>
              )}
              <ModalAmountRow>
                <span>
                  {priceDiffModal.isRefund
                    ? "Refund (excl. GST)"
                    : "Price difference"}
                </span>
                <strong>{fmtINR(priceDiffModal.priceDiff)}</strong>
              </ModalAmountRow>
              <ModalAmountRow>
                <span>GST</span>
                <strong>{fmtINR(priceDiffModal.gstOnDiff)}</strong>
              </ModalAmountRow>
              <ModalAmountDivider />
              <ModalAmountRow $total>
                <span>
                  {priceDiffModal.isRefund
                    ? "Total Refund (incl. GST)"
                    : "Total Payable"}
                </span>
                <strong>{fmtINR(priceDiffModal.totalOwed)}</strong>
              </ModalAmountRow>
              {priceDiffModal.policyApplied && (
                <ModalHelperText>
                  Policy: {priceDiffModal.policyApplied}
                </ModalHelperText>
              )}
            </ModalAmountBanner>

            {priceDiffModal.isRefund ? (
              <ModalHelperText>
                The refund will be processed offline and needs staff
                confirmation with the bank transaction ID.
              </ModalHelperText>
            ) : (
              <>
                <ModalSectionLabel>Choose payment method</ModalSectionLabel>
                <RadioGroup
                  value={priceDiffModal.paymentType}
                  onChange={(e) => {
                    const value = e?.target?.value;
                    if (!value) return;
                    setPriceDiffModal((prev) => ({
                      ...prev,
                      paymentType: value,
                    }));
                  }}
                >
                  <StyledFormControlLabel
                    value="offline"
                    control={<StyledRadio />}
                    label="Offline (Cash)"
                  />
                </RadioGroup>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="secondary" size="md" onClick={closePriceDiffModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() =>
                finalizeReschedule(
                  priceDiffModal.values,
                  priceDiffModal.paymentType,
                )
              }
            >
              {priceDiffModal.isRefund ? "Confirm & Refund" : "Confirm & Pay"}
            </Button>
          </DialogActions>
        </StyledDialog>
      )}
    </>
  );
};

export default BookingDetail;

const Header = styled.div`
  padding: 24px 28px 20px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const HeaderTopMeta = styled.div``;

const RefRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 6px;
`;

const RefText = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
`;

const SubText = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: #64748b;
`;

const SessionCard = styled.div`
  background: white;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 16px;
  padding: 20px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 20px;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
`;

const DateBlock = styled.div`
  text-align: center;
  padding-right: 20px;
  border-right: 1px solid ${COLORS.HEADER_BORDER};
`;

const DateDay = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: ${COLORS.COLOR_DARK};
`;

const DateMonth = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 4px;
`;

const SessionInfo = styled.div``;

const SessionRoom = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
`;

const SessionMeta = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
`;

const Amount = styled.div`
  text-align: right;
`;

const AmountValue = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 22px;
  font-weight: 800;
  color: ${COLORS.BTN_GREEN};
`;

const AmountSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Body = styled.div`
  padding: 28px;
  background: #f8fafc;
  min-height: 100%;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 12.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #475569;
  margin-bottom: 12px;
`;

const InfoCard = styled.div`
  background: white;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  ${media.mobile} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FieldLabel = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
`;

const FieldValue = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14.5px;
  color: ${COLORS.COLOR_DARK};
`;

const ParticipantGroup = styled.div`
  margin-bottom: 16px;
`;

const ParticipantRoleHeader = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: #475569;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const ParticipantCard = styled.div`
  background: white;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 12px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  margin-bottom: 8px;

  ${media.mobile} {
    grid-template-columns: 1.4fr 1.4fr 1fr;
  }
`;

const ParticipantText = styled.div`
  font-size: 13.5px;
  color: ${({ $strong }) => ($strong ? COLORS.COLOR_DARK : "#64748b")};
  font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
`;

const KVBox = styled.div``;

const KVRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 13.5px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};

  &:last-child {
    border-bottom: none;
  }
`;

const KVLabel = styled.span`
  color: #64748b;
`;

const KVValue = styled.span`
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLORS.BORDER_GREY};
  margin: 8px 0;
`;

const InvoiceCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 10px;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const InvoiceLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const InvoiceMeta = styled.div``;

const InvoiceNumber = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14px;
  font-weight: 700;
`;

const InvoiceType = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

const InvoiceRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const InvoiceAmount = styled.div`
  text-align: right;
`;

const InvoiceAmountValue = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
`;

const PDFButton = styled.button`
  padding: 8px 16px;
  background: #e0f2fe;
  color: ${COLORS.BTN_GREEN};
  font-weight: 700;
  font-size: 12px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;

  &:hover {
    background: #bae6fd;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-size: 13px;
  font-style: italic;
`;

export const StyledDialog = styled(Dialog)`
  .MuiPaper-root {
    border-radius: 16px;
    min-width: 360px;
  }
`;

export const ModalTitle = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
`;

export const ModalAmountBanner = styled.div`
  background: #f8fafc;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
`;

export const ModalAmountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${({ $total }) => ($total ? "15px" : "13.5px")};
  color: ${({ $total }) => ($total ? COLORS.COLOR_DARK : "#64748b")};
  padding: 4px 0;

  strong {
    color: ${({ $total }) => ($total ? COLORS.BTN_GREEN : COLORS.COLOR_DARK)};
    font-weight: 700;
  }
`;

export const ModalAmountDivider = styled.div`
  height: 1px;
  background: ${COLORS.BORDER_GREY};
  margin: 8px 0;
`;

export const ModalSectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
`;

export const ModalHelperText = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #94a3b8;
  margin-top: 10px;
  line-height: 1.5;
`;

export const StyledFormControlLabel = styled(FormControlLabel)`
  margin-bottom: 4px;

  .MuiFormControlLabel-label {
    font-family: ${theme.fonts.primaryFontRegular};
    font-size: 14px;
    color: ${COLORS.COLOR_DARK};
  }
`;

export const StyledRadio = styled(Radio)`
  &.MuiRadio-colorSecondary.Mui-checked {
    color: ${COLORS.BTN_GREEN};
  }
`;
